/**
 * EP1 / EP2 求购记录
 *
 * Routes:
 *   /ep-buy-history         → EP1
 *   /ep-buy-history?type=ep2 → EP2
 *
 * Tabs:
 *   全网成交 — 已完成 EP 交易（php_user_transaction_ep.type 0/1）
 *   我的 — 本人求购/挂卖
 *   流水 — ep1/ep2 账本
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { epAPI, type EpOrderUi } from '../api/ep';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { EPBUY_COLORS as C, TRADING_COLORS } from '@/constants';
import {
  ThemedFixedTopBar,
  ThemedPanel,
} from '../components/ui/ThemedSurface';
import {
  formatLedgerDelta,
  formatLedgerNote,
  resolveLedgerRowTitle,
} from '../utils/ledger-display';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import {
  formatEpListAmount,
  resolveEpOrderDisplayCode,
} from '../utils/ep-order-display';

type TabKey = 'global' | 'mine' | 'ledger';
type OrderRole = 'all' | 'buy' | 'sell';
type StatusFilter = 'all' | 'active' | 'done';

const GLOBAL_PAGE_SIZE = 50;

const ACTIVE_STATUSES = new Set(['open', 'claimed', 'paid', 'disputed']);
const DONE_STATUSES = new Set(['done', 'cancelled', 'timeout']);

function fmt(v: string, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function TabStrip({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: TabKey; label: string }[];
  value: TabKey;
  onChange: (v: TabKey) => void;
}) {
  const cols = tabs.length >= 3 ? 'grid-cols-3' : 'grid-cols-2';
  return (
    <div className={`grid ${cols} gap-1`}>
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 overflow-hidden px-1 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{ color: active ? C.gold : C.muted }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function FilterChips<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className="rounded-full border px-3 py-1 text-[11px] font-semibold transition active:scale-[0.98]"
            style={{
              borderColor: active ? `${C.gold}88` : C.line,
              background: active ? `${C.gold}18` : C.inputBg,
              color: active ? C.gold : C.muted,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const EP_LIST_CODE = '#A6D333';
const EP_LIST_TIME = '#5E6C84';

function EpBuyHistoryRow({
  order,
  roleLabel,
}: {
  order: EpOrderUi;
  roleLabel?: string;
}) {
  const displayCode = resolveEpOrderDisplayCode(order);
  const amountEp = formatEpListAmount(order.amount);

  return (
    <div className="border-b px-1 py-3" style={{ borderColor: C.line }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-mono text-sm font-semibold" style={{ color: EP_LIST_CODE }}>
            {displayCode}
          </div>
          <div className="mt-1 text-xs" style={{ color: EP_LIST_TIME }}>
            {formatTime(order.create_time)}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-sm font-semibold" style={{ color: C.text }}>
            {amountEp}EP
          </div>
          {roleLabel && (
            <div className="mt-1 text-[10px] font-semibold" style={{ color: C.muted }}>
              {roleLabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LedgerRowCard({
  row,
}: {
  row: {
    id: string;
    asset: string;
    action: string;
    delta: string;
    balance_after: string;
    note: string | null;
    create_time: string;
  };
}) {
  const { t } = useTranslation();
  const delta = formatLedgerDelta(row.delta);

  return (
    <ThemedPanel className="p-0" theme={C}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold" style={{ color: C.text }}>
            {resolveLedgerRowTitle(row.action, row.note, t)}
          </span>
          <span
            className="font-mono text-sm font-semibold"
            style={{ color: delta.isNegative ? C.red : C.green }}
          >
            {delta.text}
          </span>
        </div>
        <div className="mt-1 text-xs" style={{ color: C.muted }}>
          {t('history.balanceAfter', '余额')}: {fmt(row.balance_after)}{' '}
          {row.asset.toUpperCase()}
        </div>
        {row.note && (
          <div className="mt-0.5 truncate text-[10px]" style={{ color: C.muted }}>
            {formatLedgerNote(row.note, t)}
          </div>
        )}
        <div className="mt-0.5 text-[10px]" style={{ color: C.muted }}>
          {formatTime(row.create_time)}
        </div>
      </div>
    </ThemedPanel>
  );
}

export function EpBuyHistoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const isEp2 = searchParams.get('type') === 'ep2';
  const epType = isEp2 ? 'EP2' : 'EP1';
  const ledgerAsset = isEp2 ? 'ep2' : 'ep1';
  const accessToken = useAuthStore((s) => s.accessToken);
  const profileId = useAuthStore((s) => s.profile?.id);

  const meQuery = useQuery({
    queryKey: ['auth', 'me', 'ep-buy-history'],
    queryFn: () => authAPI.me(),
    enabled: Boolean(accessToken) && typeof profileId !== 'number',
    staleTime: 60_000,
  });

  const userId = typeof profileId === 'number' ? profileId : meQuery.data?.id;

  const [activeTab, setActiveTab] = useState<TabKey>('global');
  const [orderRole, setOrderRole] = useState<OrderRole>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [globalPage, setGlobalPage] = useState(1);

  const pageTitle = isEp2
    ? t('epBuyHistory.titleEp2', 'EP2 求购记录')
    : t('epBuyHistory.titleEp1', 'EP1 求购记录');

  const epMarket = isEp2 ? 'ep2' : 'ep1';

  const tabs = useMemo(
    () => [
      { key: 'global' as const, label: t('epBuyHistory.tabGlobal', '全网成交') },
      { key: 'mine' as const, label: t('epBuyHistory.tabMine', '我的') },
      {
        key: 'ledger' as const,
        label: t('epBuyHistory.tabLedger', { asset: epType, defaultValue: `${epType} 流水` }),
      },
    ],
    [t, epType],
  );

  const roleFilterOptions = useMemo(
    () => [
      { key: 'all' as const, label: t('common.all', '全部') },
      { key: 'buy' as const, label: t('epBuyHistory.roleBuy', '我的求购') },
      { key: 'sell' as const, label: t('epBuyHistory.roleSell', '我的挂卖') },
    ],
    [t],
  );

  const statusFilterOptions = useMemo(
    () => [
      { key: 'all' as const, label: t('common.all', '全部') },
      { key: 'active' as const, label: t('epBuyHistory.filterActive', '进行中') },
      { key: 'done' as const, label: t('epBuyHistory.filterDone', '已完成') },
    ],
    [t],
  );

  const globalDoneQuery = useQuery({
    queryKey: ['ep', 'buy-history', 'global-done', epMarket, globalPage],
    queryFn: () =>
      epAPI.listOrders({
        status: 'done',
        ep_type: epMarket,
        page: 1,
        limit: globalPage * GLOBAL_PAGE_SIZE,
      }),
    enabled: activeTab === 'global',
  });

  const mineEnabled = activeTab === 'mine' && typeof userId === 'number';
  const needBuy = orderRole === 'all' || orderRole === 'buy';
  const needSell = orderRole === 'all' || orderRole === 'sell';

  const buyOrdersQuery = useQuery({
    queryKey: ['ep', 'buy-history', 'buyer', userId],
    queryFn: () => epAPI.listOrders({ buyer_id: userId!, page: 1, limit: 100 }),
    enabled: mineEnabled && needBuy,
  });

  const sellOrdersQuery = useQuery({
    queryKey: ['ep', 'buy-history', 'seller', userId],
    queryFn: () => epAPI.listOrders({ seller_id: userId!, page: 1, limit: 100 }),
    enabled: mineEnabled && needSell,
  });

  const ledgerQuery = useQuery({
    queryKey: ['ep', 'buy-history-ledger', ledgerAsset],
    queryFn: async () =>
      (await usersAPI.getLedger({ asset: ledgerAsset, page: 1, limit: 50 })) as {
        total: number;
        rows: Array<{
          id: string;
          asset: string;
          action: string;
          delta: string;
          balance_after: string;
          note: string | null;
          create_time: string;
        }>;
      },
    enabled: activeTab === 'ledger',
  });

  const mergedOrderRows = useMemo(() => {
    const byId = new Map<string, EpOrderUi & { _role: 'buy' | 'sell' }>();
    if (needBuy) {
      for (const row of buyOrdersQuery.data?.rows ?? []) {
        byId.set(row.id, { ...row, _role: 'buy' });
      }
    }
    if (needSell) {
      for (const row of sellOrdersQuery.data?.rows ?? []) {
        if (!byId.has(row.id)) byId.set(row.id, { ...row, _role: 'sell' });
      }
    }
    return [...byId.values()].sort(
      (a, b) => new Date(b.create_time).getTime() - new Date(a.create_time).getTime(),
    );
  }, [buyOrdersQuery.data?.rows, sellOrdersQuery.data?.rows, needBuy, needSell]);

  const filteredOrderRows = useMemo(() => {
    if (statusFilter === 'all') return mergedOrderRows;
    if (statusFilter === 'active') {
      return mergedOrderRows.filter((r) => ACTIVE_STATUSES.has(r.status));
    }
    return mergedOrderRows.filter((r) => DONE_STATUSES.has(r.status));
  }, [mergedOrderRows, statusFilter]);

  const roleLabels = useMemo(
    () => ({
      buy: t('epBuyHistory.roleBuy', '我的求购'),
      sell: t('epBuyHistory.roleSell', '我的挂卖'),
    }),
    [t],
  );

  const globalRows = globalDoneQuery.data?.rows ?? [];
  const globalTotal = globalDoneQuery.data?.total ?? 0;
  const globalHasMore = globalPage * GLOBAL_PAGE_SIZE < globalTotal;

  const mineLoading =
    (needBuy && buyOrdersQuery.isLoading) || (needSell && sellOrdersQuery.isLoading);
  const mineError = buyOrdersQuery.isError || sellOrdersQuery.isError;
  const profileLoading = typeof userId !== 'number' && meQuery.isLoading;

  const isLoading =
    activeTab === 'global'
      ? globalDoneQuery.isLoading
      : activeTab === 'mine'
        ? mineLoading || profileLoading
        : ledgerQuery.isLoading;
  const isError =
    activeTab === 'global'
      ? globalDoneQuery.isError
      : activeTab === 'mine'
        ? mineError
        : ledgerQuery.isError;

  const emptyOrdersTitle =
    orderRole === 'sell'
      ? t('epBuyHistory.noSell', '暂无挂卖记录')
      : orderRole === 'buy'
        ? t('epBuyHistory.noBuy', '暂无求购记录')
        : t('epBuyHistory.noOrders', '暂无 EP 交易记录');

  const emptyOrdersHint =
    orderRole === 'sell'
      ? t('epBuyHistory.noSellHint', '在 EP 交易大厅挂卖后，记录会显示在这里。')
      : orderRole === 'buy'
        ? t('epBuyHistory.noBuyHint', '前往 EP 交易大厅认领卖单后，记录会显示在这里。')
        : t('epBuyHistory.noOrdersHint', '求购（接盘）与挂卖记录都会显示在这里。');

  return (
    <div className="relative min-h-full pb-8" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={pageTitle} onBack={() => navigate(-1)} theme={C} />

        <ThemedFixedTopBar background={TRADING_COLORS.fixedTopBg} className="space-y-3 pb-3">
          <ThemedPanel className="p-3" theme={C}>
            <p className="text-[11px] leading-relaxed" style={{ color: C.muted }}>
              {activeTab === 'global'
                ? t(
                    'epBuyHistory.hintGlobal',
                    '全网已完成的 {{epType}} 法币 EP 成交记录（历史镜像数据）。',
                    { epType },
                  )
                : t(
                    'epBuyHistory.hint',
                    '{{epType}} 法币求购通过 EP 撮合完成；交易前请先在资产变换中将 {{asset}} 转为 EP。',
                    { epType, asset: ledgerAsset.toUpperCase() },
                  )}
            </p>
          </ThemedPanel>
          <TabStrip
            tabs={tabs}
            value={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              if (tab === 'global') setGlobalPage(1);
            }}
          />
          {activeTab === 'mine' && (
            <div className="space-y-2">
              <FilterChips value={orderRole} onChange={setOrderRole} options={roleFilterOptions} />
              <FilterChips value={statusFilter} onChange={setStatusFilter} options={statusFilterOptions} />
            </div>
          )}
        </ThemedFixedTopBar>

        <main
          className={`${activeTab === 'mine' ? 'pt-[240px]' : 'pt-[200px]'}`}
        >
          {isError && (
            <div className="px-5">
              <QueryErrorNote
              message={t('epBuyHistory.loadFailed', '加载记录失败')}
              onRetry={() => {
                if (activeTab === 'global') void globalDoneQuery.refetch();
                else if (activeTab === 'mine') {
                  void buyOrdersQuery.refetch();
                  void sellOrdersQuery.refetch();
                } else void ledgerQuery.refetch();
              }}
            />
            </div>
          )}

          {isLoading && (
            <div className="space-y-2 px-5">
              <LoadingSkeleton className="h-20" />
              <LoadingSkeleton className="h-20" />
              <LoadingSkeleton className="h-20" />
            </div>
          )}

          {activeTab === 'global' && !isLoading && !isError && (
            <>
              {globalRows.length === 0 ? (
                <EmptyState
                  title={t('epBuyHistory.noGlobal', { epType, defaultValue: `暂无全网 ${epType} 成交` })}
                  description={t(
                    'epBuyHistory.noGlobalHint',
                    '已完成的 EP 交易会显示在这里。',
                  )}
                />
              ) : (
                <div className="px-5">
                  <p className="pb-2 text-center text-[10px]" style={{ color: C.muted }}>
                    {t('epBuyHistory.globalCount', '共 {{total}} 笔 · 已加载 {{loaded}} 笔', {
                      total: globalTotal,
                      loaded: globalRows.length,
                    })}
                  </p>
                  {globalRows.map((order) => (
                    <EpBuyHistoryRow key={order.id} order={order} />
                  ))}
                  {globalHasMore && (
                    <button
                      type="button"
                      disabled={globalDoneQuery.isFetching}
                      onClick={() => setGlobalPage((p) => p + 1)}
                      className="mt-2 w-full rounded-xl border py-3 text-center text-xs font-semibold active:scale-[0.98] disabled:opacity-60"
                      style={{ borderColor: C.softBorder, color: C.gold, background: C.chipBg }}
                    >
                      {globalDoneQuery.isFetching
                        ? t('common.loading', '加载中…')
                        : t('epBuyHistory.loadMore', '加载更多')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'mine' && !isLoading && !isError && (
            <div className="px-5">
              {filteredOrderRows.length === 0 ? (
                <EmptyState title={emptyOrdersTitle} description={emptyOrdersHint} />
              ) : (
                filteredOrderRows.map((order) => (
                  <EpBuyHistoryRow
                    key={order.id}
                    order={order}
                    roleLabel={orderRole === 'all' ? roleLabels[order._role] : undefined}
                  />
                ))
              )}
              <button
                type="button"
                onClick={() => navigate(`/ep-trading${isEp2 ? '?type=ep2' : ''}`)}
                className="mt-2 w-full rounded-xl border py-3 text-center text-xs font-semibold active:scale-[0.98]"
                style={{ borderColor: C.softBorder, color: C.gold, background: C.chipBg }}
              >
                {t('epBuyHistory.goTrading', '前往 {{epType}} 交易大厅', { epType })}
              </button>
            </div>
          )}

          {activeTab === 'ledger' && !isLoading && !isError && (
            <div className="space-y-2 px-5">
              {(ledgerQuery.data?.rows ?? []).length === 0 ? (
                <EmptyState
                  title={t('epBuyHistory.noLedger', { asset: epType, defaultValue: `暂无 ${epType} 流水` })}
                  description={t('epBuyHistory.noLedgerHint', '资产变换、奖励等变动会记录在此。')}
                />
              ) : (
                ledgerQuery.data!.rows.map((row) => <LedgerRowCard key={row.id} row={row} />)
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default EpBuyHistoryPage;
